import { memo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import type {
  InvestorListDTO,
  TransactionCategory,
  TransactionImportPreviewRowDTO,
  TransactionImportRoomOptionDTO,
  TransactionImportRowOverrideDTO,
} from '@inversor-hub/types';
import { transactionCategoryLabels, transactionTypeLabels } from '../../constants';
import { TransactionCategorySelect } from '../../TransactionCategorySelect';
import { formatCurrency } from '@/utils/misc';
import type { ImportRowStatus } from './types';
import { getStatusVisual } from './statusVisuals';
import { PreviewRowDetail } from './PreviewRowDetail';

interface PreviewTableRowProps {
  row: TransactionImportPreviewRowDTO;
  override: TransactionImportRowOverrideDTO | undefined;
  status: ImportRowStatus;
  investments: InvestorListDTO[];
  roomOptions: TransactionImportRoomOptionDTO[];
  showInvestmentColumn: boolean;
  showRoomColumn: boolean;
  showRoomForRow: boolean;
  onRowOverrideChange: (
    rowNumber: number,
    patch: Omit<TransactionImportRowOverrideDTO, 'rowNumber'>
  ) => void;
}

const PreviewTableRowComponent = ({
  row,
  override,
  status,
  investments,
  roomOptions,
  showInvestmentColumn,
  showRoomColumn,
  showRoomForRow,
  onRowOverrideChange,
}: PreviewTableRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const visual = getStatusVisual(status);
  const category = override?.category ?? row.draft.category ?? '';
  const concept = override?.concept ?? row.draft.concept ?? '';
  const isSkipped = Boolean(override?.skip);
  const splitLines = override?.lines;
  const isSplit = Boolean(splitLines && splitLines.length > 0);
  const shouldReviewSplit = Boolean(row.draft.splitRecommendation?.shouldReview) && !isSplit;
  const suggestedCategory = row.draft.category;
  const categoryConfidence = row.draft.categoryConfidence;
  const hasUserCategory = Boolean(override?.category) || isSplit;
  const canConfirmCategory =
    !isSkipped &&
    !hasUserCategory &&
    Boolean(suggestedCategory) &&
    (categoryConfidence === 'LOW' || categoryConfidence === 'MEDIUM');
  const selectedInvestmentId = override?.investmentId ?? row.investmentMatch.investmentId ?? '';
  const collapseColspan = 9 + Number(showInvestmentColumn) + Number(showRoomColumn);
  const tenantInvestmentId = override?.investmentId ?? row.investmentMatch.investmentId;
  const selectedRoomId = override?.roomId ?? row.tenantMatch.roomId ?? '';
  const availableRooms = roomOptions.filter(
    (room) => !tenantInvestmentId || room.investmentId === tenantInvestmentId
  );

  const date = row.draft.date ? new Date(row.draft.date) : null;
  const formattedDate =
    date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('es-ES') : '—';

  const typeLabel = row.draft.type ? transactionTypeLabels[row.draft.type] : null;
  const formattedAmount =
    row.draft.amount !== undefined && row.draft.amount !== null
      ? formatCurrency(row.draft.amount)
      : '—';

  const toggle = () => setExpanded((current) => !current);

  const toggleSkip = () => {
    onRowOverrideChange(row.rowNumber, { skip: !isSkipped });
    if (!isSkipped) setExpanded(false);
  };

  const dimmedSx = isSkipped
    ? { textDecoration: 'line-through', color: 'text.disabled' }
    : undefined;

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        sx={{
          cursor: 'pointer',
          '& > td': { borderBottom: expanded ? 'none' : undefined },
          ...visual.rowSx,
        }}
      >
        <TableCell sx={{ width: 32, p: 0, pl: 1 }}>
          <IconButton
            size="small"
            aria-label={expanded ? 'Contraer fila' : 'Expandir fila'}
            onClick={(event) => {
              event.stopPropagation();
              toggle();
            }}
          >
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: 48, color: 'text.secondary' }}>{row.rowNumber}</TableCell>
        <TableCell sx={{ width: 56 }}>
          <Tooltip title={visual.tooltip} arrow>
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
            >
              {visual.icon}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ width: 96, whiteSpace: 'nowrap', ...dimmedSx }}>{formattedDate}</TableCell>
        <TableCell sx={{ width: 80 }}>
          {typeLabel ? (
            <Chip
              label={typeLabel}
              size="small"
              variant="outlined"
              color={row.draft.type === 'INCOME' ? 'success' : 'default'}
              sx={isSkipped ? { opacity: 0.6 } : undefined}
            />
          ) : (
            '—'
          )}
        </TableCell>
        {showInvestmentColumn ? (
          <TableCell
            sx={{ width: 220, minWidth: 200 }}
            onClick={(event) => event.stopPropagation()}
          >
            <FormControl size="small" fullWidth error={!selectedInvestmentId && !isSkipped}>
              <Select
                value={selectedInvestmentId}
                displayEmpty
                disabled={isSkipped}
                aria-label={`Inversión para la fila ${row.rowNumber}`}
                onChange={(event) =>
                  onRowOverrideChange(row.rowNumber, {
                    investmentId: event.target.value || undefined,
                    roomId: undefined,
                    rentAllocations: undefined,
                  })
                }
                renderValue={(value) => {
                  const investment = investments.find((candidate) => candidate.id === value);
                  if (!investment) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Seleccionar…
                      </Typography>
                    );
                  }
                  return <InvestmentOptionLabel investment={investment} />;
                }}
              >
                <MenuItem value="">
                  <em>Sin asignar</em>
                </MenuItem>
                {investments.map((investment) => (
                  <MenuItem key={investment.id} value={investment.id}>
                    <InvestmentOptionLabel investment={investment} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {row.investmentMatch.reason ? (
              <Tooltip title={row.investmentMatch.reason} arrow>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, cursor: 'help' }}
                  noWrap
                >
                  {override?.investmentId ? 'Asignación manual' : 'Detectada automáticamente'}
                </Typography>
              </Tooltip>
            ) : null}
          </TableCell>
        ) : null}
        <TableCell
          sx={{
            maxWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip title={concept || '—'} placement="top">
            <Typography variant="body2" noWrap sx={dimmedSx}>
              {concept || (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  —
                </Box>
              )}
            </Typography>
          </Tooltip>
        </TableCell>
        {showRoomColumn && (
          <TableCell
            sx={{ width: 230, minWidth: 210 }}
            onClick={(event) => event.stopPropagation()}
          >
            {showRoomForRow ? (
              <>
                {row.draft.type === 'INCOME' ? (
                  <FormControl size="small" fullWidth>
                    <Select
                      value={selectedRoomId}
                      displayEmpty
                      disabled={isSkipped}
                      aria-label={`Habitación para la fila ${row.rowNumber}`}
                      onChange={(event) =>
                        onRowOverrideChange(row.rowNumber, {
                          roomId: event.target.value || undefined,
                          rentAllocations: undefined,
                        })
                      }
                      renderValue={(value) => {
                        const room = availableRooms.find((candidate) => candidate.roomId === value);
                        const tenantNames =
                          row.tenantMatch.roomId === room?.roomId && row.tenantMatch.tenantName
                            ? [row.tenantMatch.tenantName]
                            : room?.tenantNames;
                        return room ? (
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {room.roomName}
                            </Typography>
                            {tenantNames && tenantNames.length > 0 && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {tenantNames.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Seleccionar habitación…
                          </Typography>
                        );
                      }}
                    >
                      <MenuItem value="">
                        <em>Sin asignar</em>
                      </MenuItem>
                      {availableRooms.length === 0 && (
                        <MenuItem disabled>No hay habitaciones con contrato</MenuItem>
                      )}
                      {availableRooms.map((room) => (
                        <MenuItem key={room.roomId} value={room.roomId}>
                          <Box>
                            <Typography variant="body2">{room.roomName}</Typography>
                            {room.tenantNames.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {room.tenantNames.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  '—'
                )}
                {row.tenantMatch.reason && row.draft.type === 'INCOME' ? (
                  <Tooltip title={row.tenantMatch.reason} arrow>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {row.tenantMatch.confidence === 'HIGH'
                        ? 'Detectada automáticamente'
                        : row.tenantMatch.confidence === 'MEDIUM'
                          ? 'Posible coincidencia · revisar'
                          : 'Habitación no detectada'}
                    </Typography>
                  </Tooltip>
                ) : null}
              </>
            ) : (
              '—'
            )}
          </TableCell>
        )}
        <TableCell
          sx={{
            width: 112,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            ...dimmedSx,
          }}
        >
          {formattedAmount}
        </TableCell>
        <TableCell sx={{ width: 280, minWidth: 240 }} onClick={(event) => event.stopPropagation()}>
          {isSplit && splitLines ? (
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Chip
                  icon={<CallSplitIcon />}
                  label="Desglosada"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ width: 'fit-content', opacity: isSkipped ? 0.6 : 1 }}
                />
                <Button
                  size="small"
                  variant="text"
                  sx={{ minWidth: 0, px: 0.5 }}
                  onClick={() => setExpanded(true)}
                  disabled={isSkipped}
                >
                  Editar
                </Button>
              </Box>
              {splitLines.map((line, index) => (
                <Typography
                  key={`${line.category}-${index}`}
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...dimmedSx, lineHeight: 1.3 }}
                >
                  {transactionCategoryLabels[line.category]} · {formatCurrency(line.amount)}
                </Typography>
              ))}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={0.75}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TransactionCategorySelect
                    value={category as TransactionCategory | ''}
                    onChange={(nextCategory) => {
                      if (nextCategory) {
                        onRowOverrideChange(row.rowNumber, {
                          category: nextCategory,
                        });
                      }
                    }}
                    preferredType={row.draft.type}
                    scope="all"
                    size="small"
                    hideLabel
                    displayEmpty
                    disabled={isSkipped}
                  />
                </Box>
                {canConfirmCategory && (
                  <Tooltip title="Confirmar la categoría sugerida" arrow placement="top">
                    <IconButton
                      size="small"
                      color="success"
                      aria-label={`Confirmar categoría sugerida para la fila ${row.rowNumber}`}
                      onClick={() =>
                        suggestedCategory &&
                        onRowOverrideChange(row.rowNumber, { category: suggestedCategory })
                      }
                    >
                      <CheckRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {canConfirmCategory && (
                <Typography variant="caption" color="text.secondary">
                  {categoryConfidence === 'LOW'
                    ? 'Sugerencia de baja confianza · confírmala o cámbiala'
                    : 'Sugerencia de confianza media · confírmala o cámbiala'}
                </Typography>
              )}
              {shouldReviewSplit && !override?.category && (
                <Chip
                  icon={<CallSplitIcon />}
                  label="Revisar desglose"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ width: 'fit-content' }}
                  onClick={() => setExpanded(true)}
                />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ width: 48, p: 0 }} onClick={(event) => event.stopPropagation()}>
          <Tooltip
            title={isSkipped ? 'Restaurar fila' : 'Omitir fila al importar'}
            arrow
            placement="left"
          >
            <IconButton
              size="small"
              aria-label={isSkipped ? 'Restaurar fila' : 'Omitir fila'}
              onClick={toggleSkip}
              color={isSkipped ? 'primary' : 'default'}
            >
              {isSkipped ? <RestoreIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow sx={visual.rowSx}>
        <TableCell colSpan={collapseColspan} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded} unmountOnExit timeout="auto">
            <PreviewRowDetail
              row={row}
              override={override}
              onConceptChange={(value) => onRowOverrideChange(row.rowNumber, { concept: value })}
              onLinesChange={(lines) =>
                onRowOverrideChange(row.rowNumber, { lines, category: undefined })
              }
              onConfirmCategory={
                canConfirmCategory && suggestedCategory
                  ? () => onRowOverrideChange(row.rowNumber, { category: suggestedCategory })
                  : undefined
              }
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const InvestmentOptionLabel = ({ investment }: { investment: InvestorListDTO }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" fontWeight={600} noWrap>
      {investment.alias}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap display="block">
      {investment.address}
    </Typography>
  </Box>
);

export const PreviewTableRow = memo(PreviewTableRowComponent);
